'use client';

import { useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import { usePathname, useSearchParams } from 'next/navigation';
import { logEvent } from 'firebase/analytics';
import { analytics } from '@/lib/firebaseConfig';
import { hubxAnalytic } from '@/lib/analytics/services/analytics';
import {
  logHubxEvent,
  COMMON_EVENTS,
  APP_EVENTS,
  processEventQueue,
} from '@/lib/analytics/services/log-hubx-event';
import { useRouteChange } from '@/lib/analytics/hooks/route-change';
import { useSingleTrigger } from '@hubxco/hubx-smartlink';
import {
  getNames,
  getUserData,
  customEventPush,
} from '@/lib/analytics/services/smartlink';
import { getRemoteConfig } from '@/helpers/getRemoteConfig';
import { formatAnalyticsSource } from '@/helpers/common';
interface UtmSource extends Record<string, string | undefined> {
  utm_source: string | undefined;
  utm_medium: string | undefined;
  utm_campaign: string | undefined;
  utm_id: string | undefined;
  utm_term: string | undefined;
  utm_content: string | undefined;
}

export function useUtmSource({
  onUtmParamsFound,
}: {
  onUtmParamsFound?: (utmParams: UtmSource) => void;
} = {}): UtmSource | null {
  const searchParams = useSearchParams();
  const calledRef = useRef(false);

  const params = useRef<UtmSource | null>(null);

  useEffect(() => {
    const utmSource = searchParams.get('utm_source');
    const utmMedium = searchParams.get('utm_medium');
    const utmCampaign = searchParams.get('utm_campaign');
    const utmId = searchParams.get('utm_id');
    const utmCampaignId = searchParams.get('utm_campaign_id');
    const utmTerm = searchParams.get('utm_term');
    const utmAdset = searchParams.get('utm_adset');
    const utmContent = searchParams.get('utm_content');
    const utmAdname = searchParams.get('utm_adname');

    const currentUtmParams: UtmSource = Object.fromEntries(
      Object.entries({
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
        utm_id: utmId ?? utmCampaignId,
        utm_term: utmTerm ?? utmAdset,
        utm_content: utmContent ?? utmAdname,
      }).filter(([_, value]) => value !== null)
    ) as UtmSource;

    if (
      utmSource === null &&
      utmMedium === null &&
      utmCampaign === null &&
      utmId === null &&
      utmTerm === null &&
      utmContent === null
    ) {
      params.current = null;
    } else {
      params.current = currentUtmParams;
    }

    if (params.current === null) {
      return;
    }

    if (!calledRef.current) {
      onUtmParamsFound?.(params.current);
      calledRef.current = true;
    }
  }, [searchParams, onUtmParamsFound]);

  return params.current;
}

export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const landingEventCalledRef = useRef(false);
  useRouteChange();

  useUtmSource({
    onUtmParamsFound: (utmParams) => {
      Object.entries(utmParams).forEach(([key, value]) => {
        if (value !== undefined) {
          Cookies.set(`gb_${key}`, value, {
            expires: 1,
            domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN || 'localhost',
            sameSite: 'None',
            secure: true,
          });
        }
      });
    },
  });

  useEffect(() => {
    if (analytics) {
      const eventParams = {
        page_path: pathname,
        page_location: window.location.href,
        page_title: document.title,
      };
      logEvent(analytics, 'page_view', eventParams);
    }
  }, [pathname]);

  useEffect(() => {
    if (landingEventCalledRef.current) {
      return;
    }

    const referrer = document.referrer;
    const currentHost = window.location.hostname;
    const isDirectOrExternal = !referrer || !referrer.includes(currentHost);

    if (isDirectOrExternal) {
      landingEventCalledRef.current = true;

      const currentUrl = window.location.href;
      const previousUrl = referrer;

      const pathSegments = pathname.split('/').filter(Boolean);
      const source = formatAnalyticsSource(
        pathSegments[pathSegments.length - 1] || 'home'
      );

      logHubxEvent({
        eventName: COMMON_EVENTS.LND_GET_STARTED,
        event_params: {
          current_url: currentUrl,
          previous_url: previousUrl,
          source: source,
        },
      });
    }
  }, [pathname, searchParams]);

  useSingleTrigger(() => {
    logHubxEvent({ eventName: COMMON_EVENTS.CUSTOM_SESSION_START });

    const startTime = Date.now();
    getRemoteConfig()
      .then((config) => {
        const endTime = Date.now();
        const amount = endTime - startTime;
        logHubxEvent({
          eventName: COMMON_EVENTS.INITIALIZATION_COMPLETE,
          event_params: {
            amount,
            source: 'remoteConfig',
            info: config ? 'success' : 'error',
          },
        });
      })
      .catch((error) => {
        const endTime = Date.now();
        const amount = endTime - startTime;
        console.error('Error fetching remote config for analytics:', error);
        logHubxEvent({
          eventName: COMMON_EVENTS.INITIALIZATION_COMPLETE,
          event_params: {
            amount,
            source: 'remoteConfig',
            info: 'error',
          },
        });
      });
  });

  useEffect(() => {
    void processEventQueue(); // Start processing the queue when the provider mounts
  }, []);

  return <>{children}</>;
}

// Re-export for convenience
export {
  hubxAnalytic,
  logHubxEvent,
  COMMON_EVENTS,
  APP_EVENTS,
  getNames,
  getUserData,
  customEventPush,
};
